import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useCreateRoom } from "@/features/chat/api/chatApi";
import { ChatRoomView } from "@/features/chat/components/ChatRoomView";
import { useAuthStore } from "@/store/auth";

/**
 * 1:1 GG chat with the screen-share peer, embedded in the viewer window. Gets
 * (or creates, deduped) the direct room for the pair, then mounts the standard
 * ChatRoomView. Direct rooms are internal-only, so when the peer is a contractor
 * the create fails and we show a friendly note instead.
 */
export function ScreenShareChatPanel({ peerId }: { peerId: string }) {
  const user = useAuthStore((s) => s.user);
  const create = useCreateRoom();
  const [roomId, setRoomId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const requestedFor = useRef<string | null>(null);

  useEffect(() => {
    if (requestedFor.current === peerId) return;
    requestedFor.current = peerId;
    setRoomId(null);
    setFailed(false);
    create
      .mutateAsync({ kind: "direct", name: "", member_ids: [peerId] })
      .then((room) => setRoomId(room.id))
      .catch(() => setFailed(true));
    // `create` (react-query mutation) is stable; re-run only when the peer changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peerId]);

  if (failed) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-center text-xs text-[var(--color-text-muted)]">
        Czat 1:1 jest dostępny tylko z pracownikami GK.
      </div>
    );
  }
  if (!roomId) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-xs text-[var(--color-text-muted)]">
        <Loader2 size={14} className="animate-spin" /> Otwieram czat…
      </div>
    );
  }

  return (
    <ChatRoomView
      roomId={roomId}
      currentUserId={user?.id}
      isDirector={user?.role === "director"}
      isCeo={!!user?.is_ceo}
      isArchivedView={false}
      showBack={false}
    />
  );
}
